// AMC Register — linked with the Hardware Register
// (G.O.(Rt) No. 717/2026/LSGD, Date: 12.03.2026)
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  useRegisterList, RegisterHeader, SearchBox, RegisterPrintActions,
  RegisterTable, RegisterEntryDialog, AddEntryButton, GovFooterNote, fetchBundle, useLocalBodyName,
} from "./shared";
import { sectionsForOption } from "@/lib/registerPdf";

const EMPTY = {
  hardware_id: "", equipment: "", amc_vendor: "", contact_person: "", contact_number: "",
  vendor_details: "", amc_start: "", amc_end: "", contract_number: "", amc_amount: "",
  service_coverage: "", status: "", remarks: "",
};

const FIELDS = [
  { name: "hardware_id", label: "Linked Hardware Asset", type: "asset", full: true },
  { name: "equipment", label: "Asset Type / Equipment" },
  { name: "amc_vendor", label: "AMC Vendor" },
  { name: "contact_person", label: "Contact Person" },
  { name: "contact_number", label: "Contact Number" },
  { name: "amc_start", label: "AMC Start Date", type: "date" },
  { name: "amc_end", label: "AMC End Date", type: "date" },
  { name: "contract_number", label: "Contract Number" },
  { name: "amc_amount", label: "AMC Amount" },
  { name: "status", label: "Status", type: "select", options: ["Active", "Expired", "Renewal Due", "Cancelled"] },
  { name: "service_coverage", label: "Service Coverage", type: "textarea" },
  { name: "vendor_details", label: "Vendor & Contact Details", type: "textarea" },
  { name: "remarks", label: "Remarks", type: "textarea" },
];

function expiryBadge(amc_end) {
  if (!amc_end) return null;
  const d = new Date(String(amc_end).slice(0, 10) + "T00:00:00Z").getTime();
  if (isNaN(d)) return null;
  const days = Math.round((d - Date.now()) / 86400000);
  if (days < 0) return <Badge className="bg-red-500/10 text-red-700 border-0 text-[10px]">Expired {Math.abs(days)}d ago</Badge>;
  if (days <= 30) return <Badge className="bg-amber-500/10 text-amber-700 border-0 text-[10px]">Expires in {days}d</Badge>;
  return null;
}

export default function AmcRegisterTab() {
  const { rows, loading, save, remove } = useRegisterList("amc");
  const localBody = useLocalBodyName();
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/hardware").then(r => setAssets(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => [r.asset_id, r.equipment, r.amc_vendor, r.contract_number, r.status]
      .some(v => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <div className="space-y-4" data-testid="amc-register-tab">
      <RegisterHeader
        title="AMC REGISTER"
        formatNo="G.O.(Rt) 717/2026/LSGD"
        localBody={localBody}
        subtitle="Annual Maintenance Contracts — linked with the Hardware Register"
        testid="amc-register"
      >
        <SearchBox value={search} onChange={setSearch} placeholder="Search vendor, asset, contract…" testid="amc-search" />
        <RegisterPrintActions sections={sectionsForOption("amc")} getBundle={fetchBundle} filename="AMC_Register.pdf" testid="amc-print" />
        <AddEntryButton onClick={() => setEditing({ ...EMPTY })} label="Add AMC" testid="amc-add-btn" />
      </RegisterHeader>

      <RegisterTable
        testid="amc-table"
        loading={loading}
        rows={filtered}
        emptyText="No AMC contracts recorded yet."
        columns={[
          { key: "sl_no", label: "Sl.", render: (r, i) => r.sl_no || i + 1 },
          { key: "asset_id", label: "Asset ID", render: r => <span className="font-mono text-xs">{r.asset_id || "—"}</span> },
          { key: "equipment", label: "Equipment", render: r => r.equipment || r.asset_type || "—" },
          { key: "amc_vendor", label: "Vendor" },
          { key: "amc_end", label: "AMC End", render: r => (
              <div className="flex items-center gap-1.5 flex-wrap">{r.amc_end || "—"}{expiryBadge(r.amc_end)}</div>
            ) },
          { key: "contract_number", label: "Contract No." },
          { key: "amc_amount", label: "Amount" },
          { key: "status", label: "Status" },
        ]}
        onEdit={r => setEditing({ ...EMPTY, ...r })}
        onDelete={async r => { if (window.confirm("Delete this AMC entry?")) await remove(r); }}
      />
      <GovFooterNote />

      <RegisterEntryDialog
        open={!!editing}
        title={editing?.id ? "Edit AMC Entry" : "Add AMC Entry"}
        description="Link a hardware asset to auto-fill Asset ID, type and equipment details in the printed register."
        fields={FIELDS}
        values={editing || EMPTY}
        onChange={setEditing}
        onClose={() => setEditing(null)}
        onSave={async () => { if (await save(editing, editing?.id ? editing : null)) setEditing(null); }}
        assets={assets}
        testid="amc-dialog"
      />
    </div>
  );
}
